//选择上传类型弹窗
import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api } from '@/api/client';
import { toast } from "sonner";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/locales';

interface KnowledgeItem {
    knowledgeId: number;
    knowledgeName: string;
    createDate: string;
}

interface UploadDialogProps {
    onSuccess?: () => void;
}

export interface UploadDialogRef {
    open: () => void;
    close: () => void;
}

export const UploadDialog = forwardRef<UploadDialogRef, UploadDialogProps>(
    ({ onSuccess }, ref) => {
        const { t } = useTranslation();
        const navigate = useNavigate();
        const fileInputRef = useRef<HTMLInputElement>(null);
        const [isOpen, setIsOpen] = useState(false);
        const [knowledgeList, setKnowledgeList] = useState<KnowledgeItem[]>([]);
        const [selectedKnowledgeId, setSelectedKnowledgeId] = useState<string>('');
        const [uploadType, setUploadType] = useState<string>('1'); // 1: PDF文件, 2: 添加MD文件, 3: 批量添加
        const [isLoading, setIsLoading] = useState(false);

        useImperativeHandle(ref, () => ({
            open: () => {
                // 重置状态为初始值
                setSelectedKnowledgeId('');
                setUploadType('1');
                setIsOpen(true);
                fetchKnowledgeList();
            },
            close: () => {
                // 重置状态为初始值
                setSelectedKnowledgeId('');
                setUploadType('1');
                setIsOpen(false);
            },
        }));

        const fetchKnowledgeList = async () => {
            try {
                setIsLoading(true);
                const response: any = await api.get('/v1/admin/knowledge/list');
                setKnowledgeList(response);

                // 如果有数据，默认选中第一条
                if (response && response.length > 0) {
                    setSelectedKnowledgeId(String(response[0].knowledgeId));
                }
            } catch (error) {
                console.error(t('admin.fetchKnowledgeListFailed'), error);
                toast.error(t('admin.fetchKnowledgeListFailed'));
            } finally {
                setIsLoading(false);
            }
        };

        // 处理文件选择
        const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
            const selectedFiles = event.target.files;
            if (selectedFiles && selectedFiles.length > 0) {
                // 跳转到上传页面并传递文件和知识库ID
                if (uploadType === '1') {
                    // PDF文件可以多选
                    navigate('/admin/fileData/uploadPdf', {
                        state: {
                            knowledgeId: selectedKnowledgeId,
                            files: Array.from(selectedFiles)
                        }
                    });
                } else if (uploadType === '3') {
                    // Excel文件只能选择一个
                    navigate('/admin/fileData/uploadExcel', {
                        state: {
                            knowledgeId: selectedKnowledgeId,
                            file: selectedFiles[0] // 只传递第一个文件
                        }
                    });
                }

                // 重置状态
                setSelectedKnowledgeId('');
                setUploadType('1');
                setIsOpen(false);
                onSuccess?.();
            }
        };

        // 触发文件选择
        const triggerFileSelect = () => {
            if (fileInputRef.current) {
                // 根据上传类型设置不同的accept属性和multiple属性
                if (uploadType === '1') {
                    fileInputRef.current.accept = ".pdf";
                    fileInputRef.current.multiple = true;
                } else if (uploadType === '3') {
                    fileInputRef.current.accept = ".xlsx, .xls";
                    fileInputRef.current.multiple = false;
                }
                fileInputRef.current.click();
            }
        };
        // const triggerFileSelect = () => {
        //     if (fileInputRef.current) {
        //         fileInputRef.current.click();
        //     }
        // };

        const handleConfirm = () => {
            if (!selectedKnowledgeId) {
                toast.error(t('admin.pleaseSelectKnowledgeBase'));
                return;
            }

            switch (uploadType) {
                case '1': // PDF文件
                    // 触发文件选择操作
                    triggerFileSelect();
                    break;
                case '2': // 添加MD文件
                    // toast.info('添加MD文件功能待开发');
                    console.log(selectedKnowledgeId);
                    // return;
                    navigate('/admin/fileData/uploadMarkdown', {
                        state: {knowledgeId: selectedKnowledgeId}
                    });
                    break;
                case '3': // 批量添加
                    triggerFileSelect();
                    break;
                default:
                    toast.error(t('admin.pleaseSelectUploadType'));
                    return;
            }
        };

        // 处理对话框关闭事件
        const handleOpenChange = (open: boolean) => {
            if (!open) {
                // 关闭时重置状态
                setSelectedKnowledgeId('');
                setUploadType('1');
            }
            setIsOpen(open);
        };

        return (
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{t('admin.selectUploadOptions')}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <label className="text-right col-span-1" style={{fontSize: '15px'}}>{t('admin.knowledgeBase')}:</label>
                            <Select
                                value={selectedKnowledgeId}
                                onValueChange={setSelectedKnowledgeId}
                                disabled={isLoading}
                            >
                                <SelectTrigger className="col-span-3 w-[210px]">
                                    <SelectValue placeholder={t('admin.pleaseSelectKnowledgeBase')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {knowledgeList.map((item) => (
                                        <SelectItem
                                            key={item.knowledgeId}
                                            value={String(item.knowledgeId)}
                                        >
                                            {item.knowledgeName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <label className="text-right col-span-1" style={{fontSize: '15px'}}>{t('admin.uploadType')}:</label>
                            <Select
                                value={uploadType}
                                onValueChange={setUploadType}
                            >
                                <SelectTrigger className="col-span-3 w-[210px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">{t('admin.pdfFile')}</SelectItem>
                                    <SelectItem value="2">{t('admin.addMdFile')}</SelectItem>
                                    <SelectItem value="3">{t('admin.batchAdd')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => {
                            // 取消时重置状态
                            setSelectedKnowledgeId('');
                            setUploadType('1');
                            setIsOpen(false);
                        }}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleConfirm} disabled={!selectedKnowledgeId || isLoading}>
                            {t('common.confirm')}
                        </Button>
                    </div>
                </DialogContent>

                {/* 隐藏的文件输入框 */}
                {uploadType==='1' && (
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf"
                        multiple
                        style={{ display: 'none' }}
                    />
                )}

                {uploadType==='3' && (
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls"
                        multiple={false}
                        style={{ display: 'none' }}
                    />
                )}

            </Dialog>
        );
    }
);

UploadDialog.displayName = "UploadDialog";
